import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  giftNotificationAmountLabel,
  giftNotificationIcon,
  giftNotificationTitle,
} from '../../utils/giftNotificationDisplay';

const _NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  max-width: 400px;
`;

const NotificationCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  margin-bottom: 15px;
  animation: slideIn 0.3s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4);
    animation: shimmer 2s infinite;
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const GiftIcon = styled.div`
  font-size: 24px;
  margin-right: 10px;
`;

const NotificationTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const NotificationMessage = styled.p`
  margin: 8px 0;
  font-size: 14px;
  opacity: 0.9;
  line-height: 1.4;
`;

const PointsAmount = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 16px;
  display: inline-block;
  margin: 10px 0;
`;

const SenderInfo = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 8px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const GiftCardNotification = ({ notification, onClose, onMarkRead }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose(notification.id);
    }, 300);
  }, [notification.id, onClose]);

  useEffect(() => {
    // Auto-hide notification after 10 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 10000);

    return () => clearTimeout(timer);
  }, [handleClose]);

  const handleMarkRead = () => {
    onMarkRead(notification.id);
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <NotificationCard>
      <CloseButton onClick={handleClose}>×</CloseButton>
      
      <NotificationHeader>
        <GiftIcon>{giftNotificationIcon(notification)}</GiftIcon>
        <NotificationTitle>{giftNotificationTitle(notification)}</NotificationTitle>
      </NotificationHeader>

      <NotificationMessage>
        {notification.message}
      </NotificationMessage>

      <PointsAmount>
        {giftNotificationAmountLabel(notification)}
      </PointsAmount>

      <SenderInfo>
        From: {notification.sender_name} ({notification.sender_phone})
      </SenderInfo>

      <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
        <button
          onClick={handleMarkRead}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Mark as Read
        </button>
        <button
          onClick={handleClose}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Dismiss
        </button>
      </div>
    </NotificationCard>
  );
};

export default GiftCardNotification;
